import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { X, Users, DollarSign, Play, Film, Eye, MousePointerClick, Clock, Mail, User, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import userCostAnalysisService from './userCostAnalysisService';
import type { UserCostDetail } from './userCostAnalysisService';

function formatBRL(value: number) {
  return value.toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' });
}

function formatUSD(value: number) {
  return `USD ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(value: number) {
  return value.toLocaleString(getInterfaceLanguage());
}

interface UserCostDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  startDate: string;
  endDate: string;
}

export function UserCostDetailModal({ isOpen, onClose, userId, startDate, endDate }: UserCostDetailModalProps) {
  useLanguage();
  const [detail, setDetail] = useState<UserCostDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !userId) {
      setDetail(null);
      return;
    }

    const fetchDetail = async () => {
      setLoading(true);
      try {
        const data = await userCostAnalysisService.getUserDetail(userId, startDate, endDate);
        setDetail(data);
      } catch (err: any) {
        toast.error(err.response?.data?.message || translateInterfaceText("Erro ao carregar detalhes do usuário."));
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [isOpen, userId, startDate, endDate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm top-[-5%]">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden max-w-[600px] w-full max-h-[85vh] overflow-y-auto">
          <div className="p-5 border-b border-slate-50 flex justify-between items-center sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-blue-600" />
            <h3 className="text-sm font-bold text-slate-900">{translateInterfaceText("Detalhes do Usuário")}</h3>
            {detail?.user?.nome && (
              <span className="text-xs font-medium text-slate-500">— {detail.user.nome} {detail.user.sobrenome || ''}</span>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-slate-400 mt-3">{translateInterfaceText("Carregando detalhes...")}</p>
          </div>
        ) : detail ? (
          <div className="p-5 space-y-5">
            {/* User Info */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              {detail.user.nome && (
                <div className="flex items-center gap-2">
                  <User size={13} className="text-slate-400" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Nome")}</span>
                  <span className="text-xs font-medium text-slate-700">{detail.user.nome} {detail.user.sobrenome || ''}</span>
                </div>
              )}
              {detail.user.email && (
                <div className="flex items-center gap-2">
                  <Mail size={13} className="text-slate-400" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Email")}</span>
                  <span className="text-xs font-medium text-slate-700">{detail.user.email}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider ml-[21px]">{translateInterfaceText("ID")}</span>
                <p className="text-xs font-mono text-slate-500 break-all">{detail.user.id_user}</p>
              </div>
              {detail.user.purchased_plan && (
                <div className="flex items-center gap-2">
                  <CreditCard size={13} className="text-slate-400" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Plano")}</span>
                  <span className="text-xs font-medium text-slate-700">{detail.user.purchased_plan}</span>
                </div>
              )}
            </div>

            {/* Metrics */}
            <div>
              <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-3">{translateInterfaceText("Métricas de Uso")}</span>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Play size={12} className="text-blue-500" />
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">{translateInterfaceText("Plays")}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-900">{formatNumber(detail.user.metrics.totalPlays)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Clock size={12} className="text-orange-500" />
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">{translateInterfaceText("Retenção")}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-900">{formatNumber(detail.user.metrics.totalRetention)}s</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Eye size={12} className="text-purple-500" />
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">{translateInterfaceText("Visualizações")}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-900">{formatNumber(detail.user.metrics.totalViews)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Eye size={12} className="text-indigo-500" />
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">{translateInterfaceText("Views Únicos")}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-900">{formatNumber(detail.user.metrics.totalUniqueViews)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <MousePointerClick size={12} className="text-cyan-500" />
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">{translateInterfaceText("Cliques")}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-900">{formatNumber(detail.user.metrics.totalClicks)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Film size={12} className="text-emerald-500" />
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">{translateInterfaceText("Vídeos")}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-900">{formatNumber(detail.user.metrics.videoCount)}</p>
                </div>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div>
              <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-3">{translateInterfaceText("Detalhamento de Custos")}</span>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
                  <span className="text-[10px] font-semibold text-orange-400 uppercase block mb-1">{translateInterfaceText("Networking")}</span>
                  <p className="text-sm font-bold text-orange-700">{formatBRL(detail.user.costBreakdown.networking.brl)}</p>
                  <p className="text-[10px] text-orange-400">{formatUSD(detail.user.costBreakdown.networking.usd)}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                  <span className="text-[10px] font-semibold text-blue-400 uppercase block mb-1">{translateInterfaceText("Compute")}</span>
                  <p className="text-sm font-bold text-blue-700">{formatBRL(detail.user.costBreakdown.compute.brl)}</p>
                  <p className="text-[10px] text-blue-400">{formatUSD(detail.user.costBreakdown.compute.usd)}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                  <span className="text-[10px] font-semibold text-emerald-400 uppercase block mb-1">{translateInterfaceText("Storage")}</span>
                  <p className="text-sm font-bold text-emerald-700">{formatBRL(detail.user.costBreakdown.storage.brl)}</p>
                  <p className="text-[10px] text-emerald-400">{formatUSD(detail.user.costBreakdown.storage.usd)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase block mb-1">{translateInterfaceText("Other")}</span>
                  <p className="text-sm font-bold text-slate-700">{formatBRL(detail.user.costBreakdown.other.brl)}</p>
                  <p className="text-[10px] text-slate-400">{formatUSD(detail.user.costBreakdown.other.usd)}</p>
                </div>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">{translateInterfaceText("Total USD")}</span>
                <span className="text-xs font-semibold text-slate-700">{formatUSD(detail.user.totalCostUSD)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">{translateInterfaceText("Total BRL")}</span>
                <span className="text-xs font-semibold text-slate-700">{formatBRL(detail.user.totalCostBRL)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">{translateInterfaceText("Impostos Estimados")}</span>
                <span className="text-xs font-semibold text-amber-600">{formatBRL(detail.user.estimatedTaxBRL)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-700">{translateInterfaceText("Total c/ Impostos")}</span>
                <span className="text-sm font-bold text-slate-900">{formatBRL(detail.user.totalWithTaxBRL)}</span>
              </div>
            </div>

            {/* Global Context */}
            {detail.globalContext && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <span className="text-[11px] text-blue-400 font-bold uppercase tracking-wider block mb-2">{translateInterfaceText("Contexto Global")}</span>
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-blue-600">{translateInterfaceText("Custo médio por usuário")}</span>
                    <span className="text-[11px] font-semibold text-blue-700">{formatUSD(detail.globalContext.averageCostPerUserUSD)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-blue-600">{translateInterfaceText("Custo total plataforma")}</span>
                    <span className="text-[11px] font-semibold text-blue-700">{formatUSD(detail.globalContext.totalPlatformCostUSD)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default UserCostDetailModal;
